import type { Me } from "@contract/firm/me";
import { Anchor, SimpleGrid, Stack } from "@mantine/core";
import * as React from "react";
import { Link } from "react-router";
import { useMe } from "../api/MeContext";
import { fetchMembers } from "../api/members";
import { env } from "../env";
import { firmCanHaveTeam } from "../layout/nav";
import { Notice } from "../ui/Notice";
import { PANEL_SPACE } from "../ui/tokens";
import { BrandStatusCard } from "./home/BrandStatusCard";
import { UsageCard } from "./home/UsageCard";
import { YourDetailsCard } from "./home/YourDetailsCard";
import { OnboardingWizard } from "./onboarding/OnboardingWizard";
import { dismissedKey, isNewFirm } from "./onboarding/onboardingModel";
import { homeAlerts, type HomeAlert, type TeamFacts } from "./homeModel";
import { invitesExpiringSoon } from "./membersModel";

/** Admins of a firm that can have a team: who else is in or invited. Null otherwise, or
 * while it loads (Home renders without it rather than waiting). */
function useTeamFacts(me: Me | null, now: Date, load = fetchMembers): TeamFacts {
  const [facts, setFacts] = React.useState<TeamFacts>(null);
  const eligible = Boolean(me && me.capabilities.includes("manage_members") && firmCanHaveTeam(me));
  React.useEffect(() => {
    if (!eligible || !me) return undefined;
    let alive = true;
    load()
      .then((d) => {
        if (!alive) return;
        setFacts({
          others: d.members.filter((m) => m.userId !== me.userId).length,
          invites: d.invites.length,
          expiringSoon: invitesExpiringSoon(d, now),
        });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [eligible, me?.userId]);
  return facts;
}

/** One alert, with its way onward when it has one. */
function AlertNotice({ alert }: { alert: HomeAlert }) {
  const a = alert.action;
  return (
    <Notice tone="warning">
      {alert.text}
      {a ? (
        <>
          {" "}
          {"href" in a ? (
            <Anchor href={a.href} target="_blank" rel="noopener noreferrer" c="inherit" td="underline" fw={400}>
              {a.label}
            </Anchor>
          ) : (
            <Anchor component={Link} to={a.to} c="inherit" td="underline" fw={400}>
              {a.label}
            </Anchor>
          )}
        </>
      ) : null}
    </Notice>
  );
}

/** Home (firm-model spec §10.2; Dan's frame 15187:6107, 2026-10-02): alerts on top (the
 * Needs attention items, the add-in sign-in, an uninvited team), then Your Details over
 * Usage, beside Brand Configuration (Dan 2026-10-02). A new firm's admin gets the onboarding wizard over it. */
export function HomePage({ now = new Date(), email = "", userName = null }: { now?: Date; email?: string; userName?: string | null }) {
  const { state, reload } = useMe();
  const me = state.status === "ready" ? state.me : null;
  const team = useTeamFacts(me, now);
  const [wizardOpen, setWizardOpen] = React.useState(false);
  // A new firm's admin gets the onboarding wizard once (until they skip it in this browser).
  React.useEffect(() => {
    if (me && isNewFirm(me) && !readDismissed(me.userId)) setWizardOpen(true);
  }, [me?.userId]);
  if (!me) return null;
  const closeWizard = () => {
    setWizardOpen(false);
    writeDismissed(me.userId);
    reload();
  };
  const alerts = homeAlerts(me, now, team, { appSourceUrl: env.appSourceUrl, envLabel: env.envLabel });

  return (
    <Stack gap={PANEL_SPACE.page}>
      {alerts.map((a) => (
        <AlertNotice key={a.key} alert={a} />
      ))}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing={PANEL_SPACE.page} verticalSpacing={PANEL_SPACE.page}>
        <Stack gap={PANEL_SPACE.page}>
          <YourDetailsCard me={me} now={now} onRenamed={reload} />
          <UsageCard me={me} now={now} />
        </Stack>
        <div>
          <BrandStatusCard canEdit={me.capabilities.includes("edit_standards")} />
        </div>
      </SimpleGrid>
      {wizardOpen ? <OnboardingWizard me={me} email={email} userName={userName} onClose={closeWizard} /> : null}
    </Stack>
  );
}

/** The wizard's "skipped" mark, per user, in this browser; storage can be unavailable. */
function readDismissed(userId: string): boolean {
  try {
    return window.localStorage.getItem(dismissedKey(userId)) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(userId: string): void {
  try {
    window.localStorage.setItem(dismissedKey(userId), "1");
  } catch {
    // Without storage the wizard simply offers itself again on the next visit.
  }
}
